import _ from "lodash";
import Promise from "bluebird";
import Backbone from "backbone";
import FacetedSearchResultCollection from "scripts/collections/facetedSearchResultCollection";
import BreadcrumbModel from "scripts/models/breadcrumbModel";
import UnauthorizedSyncError from "scripts/exceptions/unauthorizedSyncError";
import inject from "scripts/ioc/inject";
import BaseView from "scripts/views/baseView";
import MediaListView from "scripts/views/mediaListView";
import MediaSalvattoreGridView from "scripts/views/mediaSalvattoreGridView";
import PagerView from "scripts/views/ellipsisPagerView";
import FacetedSearchResultsMenuView from "scripts/views/search/facetedSearchResultsMenuView";
import CurationMultiSelectView from "scripts/views/curationMultiSelectView";
import {
  pushFragment,
  redirectToFragment,
  getCurrentFragment,
} from "scripts/utils/routerHelpers";

import templateFacetedSearchResultsPage from "templates/search/facetedSearchResultsPage.hbs";

const chan = Backbone.Radio.channel;

class FacetedSearchResultsPageView extends BaseView {
  constructor(
    options,
    breadcrumbService = inject("breadcrumbService"),
    deviceService = inject("deviceService"),
  ) {
    super(options);

    this.breadcrumbService = breadcrumbService;
    this.deviceService = deviceService;

    chan("tracking").on("viewer:click", this.trackSearchClick, this);
    chan("tracking").on("detail:click", this.trackSearchClick, this);

    const pageIndex = this.model.get("pageIndex");

    this.collection = new FacetedSearchResultCollection([], {
      path: "/search/v2",
      state: {
        currentPage: pageIndex,
      },
    });

    /**
     * Hack to satisfy Backbone.PageableCollection._checkState
     * if starting from a page other than 0.
     */
    if (pageIndex > 0) {
      this.collection.links[pageIndex] = this.collection.url;
    }

    this.menuView = this.addSubView(
      new FacetedSearchResultsMenuView({
        model: this.model,
        collection: this.collection,
      }),
    );

    this.curationMultiSelectView = this.addSubView(
      new CurationMultiSelectView(),
    );

    this.model.on(
      "change:queryParams",
      queryParams => {
        console.log("Search query params changed, new params: %O", queryParams);

        // when query params change, links are reset by setting the mode. It
        // should already be infinite.
        this.collection.switchMode("infinite");

        chan("tracking").trigger("search");

        this.model.set("pageIndex", 0);

        this.updateLocation();

        if (
          this.syncCollectionPromise &&
          this.syncCollectionPromise.isPending()
        ) {
          console.log("Cancelling pending search results collection sync");

          this.syncCollectionPromise.cancel();
        } else {
          chan("display").trigger("bodyViewLoading");
          this.model.set("loading", true);
        }

        this.syncCollectionPromise = this.syncCollection()
          .then(() => {
            this.menuView.update();
            this.showSubPageView(this.createSubPageView());

            chan("display").trigger("bodyViewLoaded");
            this.model.set("loading", false);
          })
          .catch(Promise.CancellationError, () => {
            console.log("Search results collection sync cancelled");
          })
          .catch(UnauthorizedSyncError, () => {
            console.log(
              "Faceted search collection sync caught unauthorized error, logging user out...",
            );
            return chan("security").request("logout", false);
          })
          .catch(error => {
            return chan("display").request("showErrorPage", error);
          });
      },
      this,
    );

    this.model.on(
      "change:presentationType",
      (model, presentationType) => {
        this.updateLocation();

        this.onPresentationTypeChangeSaveUserPreference(
          model,
          presentationType,
        );
        this.showSubPageView(this.createSubPageView());
      },
      this,
    );
  }

  get events() {
    return {
      keyup: "onKeyup",
      "DOMMouseScroll #bb-faceted-search-facet-menu-region-overlay":
        "onOverlayScroll",
      "touchmove #bb-faceted-search-facet-menu-region-overlay":
        "onOverlayScroll",
    };
  }

  onOverlayScroll(event) {
    event.preventDefault();
    event.stopPropagation();
    return false;
  }

  onKeyup(e) {
    if (e.key === "Tab") {
      this.menuView.expandRefineMenu();
    }
  }

  updateLocation() {
    const urlFragment = this.getUrlFragment();

    pushFragment(urlFragment);
    chan("history").request("replaceFragment", urlFragment);

    this.breadcrumbService.updateBreadcrumb();
  }

  getUrlFragment(pageIndex) {
    if (pageIndex === void 0) {
      pageIndex = this.model.get("pageIndex");
    }

    const url = `/search-results/${encodeURIComponent(
      this.getQueryUrlParams(),
    )}/${this.model.get("presentationType")}`;

    if (pageIndex !== 0) {
      return `${url}/${pageIndex + 1}`;
    } else {
      return url;
    }
  }

  getQueryUrlParams() {
    const queryParams = this.model.get("queryParams");

    const searchTerm = this.getSearchTerm();
    return (searchTerm ? [`g=${searchTerm}`] : [])
      .concat(
        Object.keys(queryParams)
          .filter(param => param !== "g")
          .map(param => `${param}=${encodeURIComponent(queryParams[param])}`),
      )
      .join("&");
  }

  getSearchTerm() {
    const queryParams = this.model.get("queryParams");
    const searchTerm = queryParams["g"];
    return searchTerm ? `${encodeURIComponent(searchTerm)}` : undefined;
  }

  isFullTextSearch() {
    const queryParams = this.model.get("queryParams");
    return (
      queryParams["full-text"] &&
      decodeURIComponent(queryParams["full-text"]) === "true"
    );
  }

  get template() {
    return templateFacetedSearchResultsPage;
  }

  sync() {
    return this.syncCollection().then(() => {
      this.subPageView = this.addSubView(this.createSubPageView());
    });
  }

  syncCollection() {
    this.collection.state.currentPage = this.model.get("pageIndex");

    this.collection.queryParams = _.merge(
      {
        platform: () => this.deviceService.platform,
        limit: 20,
        offset:
          this.collection.state.currentPage * this.collection.state.pageSize,
      },
      this.model.get("queryParams"),
    );

    return this.collection
      .fetch()
      .cancellable()
      .then(o => {
        return o;
      });
  }

  render() {
    this.$el.html(this.template());

    this.menuView
      .attachTo($("#bb-faceted-search-results-menu-region"))
      .render();

    this.subPageView
      .attachTo($("#bb-faceted-search-results-sub-page-region"))
      .render();

    this.curationMultiSelectView
      .attachTo($("#bb-faceted-search-curation-multiselect-view"))
      .render();

    return this;
  }

  showSubPageView(subPageView) {
    return this.showSubView(
      "subPageView",
      subPageView,
      $("#bb-faceted-search-results-sub-page-region"),
    ).finally(value => {
      $(window).scrollTop(0);
      return value;
    });
  }

  createSubPageView() {
    let subPage;

    let presentationType = this.model.get("presentationType");

    let pageViewOptions = {
      hasPreviousPage: this.collection.hasPreviousPage(),
      hasNextPage: this.collection.hasNextPage(),
      pageNumber: this.model.get("pageIndex") + 1,
      totalPages: Math.ceil(
        this.collection.totalRecords / this.collection.state.pageSize,
      ),
    };

    const searchTerm = this.getSearchTerm();
    const viewerParams =
      searchTerm && this.isFullTextSearch()
        ? `searchTerm=${encodeURIComponent(searchTerm)}`
        : undefined;

    if (presentationType === "list") {
      subPage = new PagerView(
        _.extend(
          {
            mediaView: new MediaListView({
              collection: this.collection,
              viewerParams: viewerParams,
            }),
          },
          pageViewOptions,
        ),
      );
    } else {
      subPage = new PagerView(
        _.extend(
          {
            mediaView: new MediaSalvattoreGridView({
              collection: this.collection,
              viewerParams: viewerParams,
              facetedSearchGrid: true,
            }),
          },
          pageViewOptions,
        ),
      );
    }

    subPage.on("pageSequenceChanged", this.goPageSequence, this);

    return subPage;
  }

  getPreviousPageUrl() {
    if (this.collection.hasPreviousPage()) {
      return this.getUrlFragment(this.collection.state.currentPage - 1);
    } else {
      return false;
    }
  }

  getNextPageUrl() {
    if (this.collection.hasNextPage()) {
      return this.getUrlFragment(this.collection.state.currentPage + 1);
    } else {
      return false;
    }
  }

  goPageSequence(pageSequence) {
    redirectToFragment(this.getUrlFragment(pageSequence - 1));
  }

  getBreadcrumbInfo() {
    return {
      breadcrumbModel: new BreadcrumbModel({
        text: '<i class="bbico bbico-search" aria-label="current search"></i>',
        path: "#" + getCurrentFragment(),
        topLevel: true,
      }),
      hide: true,
    };
  }

  trackSearchClick(model) {
    chan("tracking").trigger("search:click", {
      mediaId: model.get("id"),
    });
  }

  getDocumentTitle() {
    return "Search Results";
  }
}

export default FacetedSearchResultsPageView;
